(function () {
  'use strict';

  const gsap = window.gsap;
  const easePower3 = window.Universe.easePower3;

  // --- Localization (inner class) ---

  class Localization {
    constructor(switcher) {
      this.DOM = {};
      this.DOM.switcher = switcher;
      this.DOM.form = this.DOM.switcher.querySelector('.js-localization-form');
      this.DOM.toggle = this.DOM.switcher.querySelector('.js-localization-toggle');
      this.DOM.list = this.DOM.switcher.querySelector('.js-localization-list');
      this.DOM.links = this.DOM.switcher.querySelectorAll('.js-localization-link');
      this.DOM.close = this.DOM.switcher.querySelector('.js-localization-close');
      this.DOM.value = this.DOM.switcher.querySelector('.js-localization-value');

      this.from = this.DOM.list.dataset.from;
      this.isOpen = false;
      this.value = this.DOM.value.value;

      this.onToggle = this.onToggle.bind(this);
      this.onItemClick = this.onItemClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);

      this.setup();
    }

    setup() {
      this.DOM.toggle.addEventListener('click', this.onToggle);
      this.DOM.close.addEventListener('click', this.onToggle);

      for (let i = 0; i < this.DOM.links.length; i++) {
        this.DOM.links[i].addEventListener('click', this.onItemClick);
      }
    }

    onToggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.isOpen = true;
      this.DOM.toggle.setAttribute('aria-expanded', 'true');
      this.DOM.close.setAttribute('tabindex', '0');
      this.setListInteractivity(true);

      gsap.set(this.DOM.close, {
        pointerEvents: 'all',
        zIndex: 50,
      });

      gsap.to(this.DOM.list, {
        opacity: 1,
        y: 0,
        pointerEvents: 'all',
        duration: 0.4,
        ease: easePower3,
      });

      document.addEventListener('keydown', this.onKeyDown);
    }

    close() {
      this.isOpen = false;
      this.DOM.toggle.setAttribute('aria-expanded', 'false');
      this.DOM.close.setAttribute('tabindex', '-1');
      this.setListInteractivity(false);

      gsap.set(this.DOM.close, {
        pointerEvents: 'none',
        zIndex: -1,
      });

      gsap.to(this.DOM.list, {
        opacity: 0,
        y: this.from == 'top' ? 10 : -10,
        pointerEvents: 'none',
        duration: 0.4,
        ease: easePower3,
      });

      document.removeEventListener('keydown', this.onKeyDown);
    }

    onKeyDown(event) {
      const isTabPressed = event.key === 'Tab' || event.keyCode === 9;

      if (event.code === 'Escape') {
        this.close();
        this.DOM.toggle.focus();
        return;
      }

      if (isTabPressed) {
        const focusableEls = [];
        for (let i = 0; i < this.DOM.links.length; i++) {
          focusableEls.push(this.DOM.links[i]);
        }
        focusableEls.push(this.DOM.close);
        const firstFocusableEl = focusableEls[0];
        const lastFocusableEl = focusableEls[focusableEls.length - 1];

        if (event.shiftKey) {
          if (document.activeElement === firstFocusableEl) {
            lastFocusableEl.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusableEl) {
            firstFocusableEl.focus();
            event.preventDefault();
          }
        }
      }
    }

    onItemClick(event) {
      event.preventDefault();

      this.value = event.currentTarget.dataset.value;
      this.DOM.value.value = this.value;
      if (this.DOM.form) {
        this.DOM.form.submit();
      }
    }

    destroy() {
      if (this.DOM) {
        this.DOM.toggle.removeEventListener('click', this.onToggle);
        this.DOM.close.removeEventListener('click', this.onToggle);
        document.removeEventListener('keydown', this.onKeyDown);

        for (let i = 0; i < this.DOM.links.length; i++) {
          this.DOM.links[i].removeEventListener('click', this.onItemClick);
        }

        gsap.killTweensOf(this.DOM.list);
        gsap.killTweensOf(this.DOM.toggle);
        gsap.killTweensOf(this.DOM.close);

        this.DOM = undefined;
      }
    }

    setListInteractivity(enable) {
      if (!this.DOM || !this.DOM.list) return;

      const supportsInert = 'inert' in HTMLElement.prototype;
      this.DOM.list.setAttribute('aria-hidden', enable ? 'false' : 'true');

      if (supportsInert) {
        this.DOM.list.inert = !enable;
      } else {
        const focusables = this.DOM.list.querySelectorAll('a, button, input, select, textarea, [tabindex]');
        focusables.forEach((el) => {
          if (enable) {
            if (el.hasAttribute('data-prev-tabindex')) {
              const prev = el.getAttribute('data-prev-tabindex');
              if (prev === '') el.removeAttribute('tabindex');
              else el.setAttribute('tabindex', prev);
              el.removeAttribute('data-prev-tabindex');
            } else if (el.getAttribute('tabindex') === '-1') {
              el.removeAttribute('tabindex');
            }
          } else {
            if (!el.hasAttribute('data-prev-tabindex')) {
              el.setAttribute('data-prev-tabindex', el.getAttribute('tabindex') || '');
            }
            el.setAttribute('tabindex', '-1');
          }
        });
      }
    }
  }

  // --- LocalizationManager ---

  class LocalizationManager {
    constructor() {
      this.init();
    }

    setup() {
      for (let i = 0; i < this.DOM.switchers.length; i++) {
        this.instances.push(new Localization(this.DOM.switchers[i]));
      }
    }

    reinit() {
      this.destroy();
      this.init();
    }

    destroy() {
      if (this.DOM) {
        for (let i = 0; i < this.instances.length; i++) {
          this.instances[i].destroy();
        }
        this.instances = [];
        this.DOM = undefined;
      }
    }

    init(container = document) {
      const switchers = container.querySelectorAll('.js-localization-switcher');

      if (switchers.length) {
        this.DOM = {};
        this.DOM.switchers = switchers;

        this.instances = [];

        this.setup();
      }
    }
  }

  window.Universe.LocalizationManager = LocalizationManager;
})();
